import { useMemo, useEffect } from 'react';

import manualInputsRegistry from '../manualInputsRegistry';
import { getManualInput } from '../utils';
import { MANUAL_INPUT_TYPE } from '../const';

export function RatioInput({ data, ...props }) {
  const { prefix, watch, labelProps, setValue, getValues } = props ?? {};

  const ratioField = data?.fields?.[0];
  const step = data?.ranges?.step;
  const { numerator, denominator, separator, units } = ratioField;

  const observationFields = useMemo(() => {
    return [numerator, denominator];
  }, [denominator, numerator]);

  const watchedNumerator = watch(`${prefix}.numerator`);
  const watchedDenominator = watch(`${prefix}.denominator`);
  const watchedUnit = watch(`${prefix}.unit`);

  const usedPrefix = props?.namePrefix ?? prefix;

  useEffect(() => {
    if (observationFields?.length && !Boolean(props?.namePrefix)) {
      setValue(`${usedPrefix}.observations[0]`, {
        ...numerator,
        value: watchedNumerator,
        unit: watchedUnit,
      });
      setValue(`${usedPrefix}.observations[1]`, {
        ...denominator,
        value: watchedDenominator,
        unit: watchedUnit,
      });
    }
    if (Boolean(props?.namePrefix)) {
      setValue(`${prefix}.${props.namePrefix}.observations[0]`, {
        ...numerator,
        value: watchedNumerator,
        unit: watchedUnit,
      });
      setValue(`${prefix}.${props.namePrefix}.observations[1]`, {
        ...denominator,
        value: watchedDenominator,
        unit: watchedUnit,
      });
    }
  }, [
    denominator,
    numerator,
    observationFields?.length,
    usedPrefix,
    setValue,
    watchedDenominator,
    watchedNumerator,
    watchedUnit,
    props.namePrefix,
    getValues,
    prefix,
    data,
  ]);

  const selectUnitInput = getManualInput(MANUAL_INPUT_TYPE.SELECT, {
    ...props,
    ...units,
    prefix,
    name: 'unit',
    options: units?.options,
    label: '',
    className: 'w-full',
    groupClassName: 'w-[9.5rem] max-w-[11.5rem] md:w-full',
  });

  const stringUnitInput = (
    <div className="h-16 items-center px-2 pt-8 text-smd-h4 text-smd-gray-darker sm:block">
      {units}
    </div>
  );

  const getUnitInput = () => {
    if (units?.options?.length) {
      return selectUnitInput;
    } else if (typeof units === 'string') {
      return stringUnitInput;
    }
    return null;
  };

  return (
    <div className="grid grid-cols-[1fr_20px_1fr_1fr] gap-x-2 gap-y-2">
      <div className="smd-label">{numerator?.label}</div>
      <div></div>
      <div className="smd-label">{denominator?.label}</div>
      <div></div>
      <div>
        {getManualInput(MANUAL_INPUT_TYPE.NUMBER, {
          ...props,
          ...numerator,
          prefix,
          watch,
          field: numerator,
          name: 'numerator',
          label: null,
          placeholder: numerator?.placeholder,
          className: 'w-[50px]',
          labelProps: labelProps,
          defaultValue: getValues?.(prefix)?.numerator,
          step: step,
        })}
      </div>
      <div className="flex items-start justify-center pt-2 text-smd-h4 text-smd-gray-darker">
        {separator}
      </div>
      <div>
        {getManualInput(MANUAL_INPUT_TYPE.NUMBER, {
          ...props,
          ...denominator,
          prefix,
          watch,
          field: denominator,
          name: 'denominator',
          label: null,
          placeholder: denominator?.placeholder,
          className: 'w-full',
          labelProps: labelProps,
          defaultValue: getValues?.(prefix)?.denominator,
          step: step,
        })}
      </div>
      <div>{getUnitInput()}</div>
    </div>
  );
}

manualInputsRegistry.register(MANUAL_INPUT_TYPE.RATIO, RatioInput);
